import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ArrowLeft, ShoppingBag } from 'lucide-react';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getTotalItems } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <div className="text-center" data-testid="empty-cart">
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1A3626] mb-4">Your cart is empty</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors"
            data-testid="continue-shopping-button"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#1A3626] hover:text-[#C05A3E] mb-6 transition-colors"
          data-testid="back-to-shopping"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Continue Shopping</span>
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A3626] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="cart-title">
          Your Order ({getTotalItems()} items)
        </h1>

        <div className="space-y-4 mb-8">
          {cart.map((item, index) => (
            <div key={`${item.product.id}-${item.unit}`} className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200" data-testid={`cart-item-${index}`}>
              <div className="flex gap-4">
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                  <img
                    src={item.product.image_url || 'https://via.placeholder.com/100'}
                    alt={item.product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-[#1A3626] mb-2" data-testid={`cart-item-name-${index}`}>
                    {item.product.name}
                  </h3>
                  <p className="text-[#2E7D32] font-bold mb-2" data-testid={`cart-item-price-${index}`}>
                    ₹{item.product.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600 mb-3" data-testid={`cart-item-unit-${index}`}>
                    Unit: {item.unit}
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.unit, item.quantity - 1)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-[#1A3626] transition-colors"
                        data-testid={`cart-decrease-${index}`}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="px-4 py-2 min-w-[3rem] text-center" data-testid={`cart-quantity-${index}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.unit, item.quantity + 1)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-[#1A3626] transition-colors"
                        data-testid={`cart-increase-${index}`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => removeFromCart(item.product.id, item.unit)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      data-testid={`cart-remove-${index}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => navigate('/checkout')}
          className="w-full bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-4 font-semibold text-lg transition-colors"
          data-testid="proceed-to-checkout-button"
        >
          Proceed to Place Order
        </button>
      </div>
    </div>
  );
};

export default CartPage;